import { DialogProps as MuiDialogProps } from '@mui/material';
import { useIsMobile } from 'shared/hooks';

import DialogBottomSheet from './DialogBottomSheet';
import DialogFullscreen from './DialogFullscreen';
import SimpleDialog from './SimpleDialog';

export interface DialogProps extends Omit<MuiDialogProps, 'fullWidth' | 'title' | 'maxWidth' | 'onClose'> {
    /**
     * Defines the semantic type of the dialog and determines how it is rendered on mobile vs desktop.
     *
     * - `"confirm"` – Small centered dialog used for simple confirmation actions (e.g. Yes/No, Apply/Cancel, Delete).
     * - `"dialog"` – For mobile: Bottom sheet dialog for short, contextual actions or small option lists. For desktop: Standard centered dialog.
     * - `"largeContent"` – For mobile: Fullscreen dialog for larger forms or detailed content. For desktop: Larger centered dialog.
     * Use `confirm` for critical or simple actions, `dialog` for compact options, and `largeContent` when the user is expected to focus on larger content.
     */
    variant?: 'confirm' | 'dialog' | 'largeContent';

    onClose?: () => void;
    title?: React.ReactNode;
    subtitle?: React.ReactNode;
    actions?: React.ReactNode;
}

const mobileVariantToModeMap = {
    confirm: 'small',
    dialog: 'bottomSheet',
    largeContent: 'fullscreen'
} as const;

const desktopVariantToModeMap = {
    confirm: 'small',
    dialog: 'medium',
    largeContent: 'large'
} as const;

export default function Dialog({ variant, ...rest }: DialogProps) {
    const isMobile = useIsMobile();
    const mode = (isMobile ? mobileVariantToModeMap : desktopVariantToModeMap)[variant || 'dialog'];

    switch (mode) {
        case 'small':
            return <SimpleDialog size="xs"
                { ...rest } />;
        case 'medium':
            return <SimpleDialog size="sm"
                { ...rest } />;
        case 'large':
            return <SimpleDialog size="md"
                { ...rest } />;
        case 'fullscreen':
            return <DialogFullscreen { ...rest } />;
        case 'bottomSheet':
            return <DialogBottomSheet { ...rest } />;
    }
}
